import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OnboardingPage from "@/pages/OnboardingPage";
import PricingPage from "@/pages/PricingPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCancelPage from "@/pages/BillingCancelPage";
import Dashboard from "@/pages/Dashboard";
import PlanOverview from "@/pages/PlanOverview";
import WeekDetail from "@/pages/WeekDetail";
import WorkoutDetail from "@/pages/WorkoutDetail";
import RecipeDetail from "@/pages/RecipeDetail";
import ShoppingList from "@/pages/ShoppingList";
import ChatList from "@/pages/ChatList";
import ChatThread from "@/pages/ChatThread";
import BackCheckIn from "@/pages/BackCheckIn";
import Stats from "@/pages/Stats";
import Settings from "@/pages/Settings";
import Workouts from "@/pages/Workouts";
import History from "@/pages/History";
import KnowledgeBase from "@/pages/KnowledgeBase";
import BodyComposition from "@/pages/BodyComposition";
import Nutrition from "@/pages/Nutrition";
import NutritionArchive from "@/pages/NutritionArchive";
import NutritionDayDetail from "@/pages/NutritionDayDetail";
import MealDetail from "@/pages/MealDetail";
import Reviews from "@/pages/Reviews";
import { LanguageSync } from "@/i18n/LanguageSync";
import { ToastContainer } from "@/components/ui/Toast";
import { AdminRoute } from "@/components/AdminRoute";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminAiSpend from "@/pages/admin/AdminAiSpend";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminCosts from "@/pages/admin/AdminCosts";
import { AnalyticsConsent } from "@/components/AnalyticsConsent";
import { RouteAnalytics } from "@/components/RouteAnalytics";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Phase 24.7 Step 6 — RequireAuth extracted to components/RequireAuth.tsx
// for unit-test isolation. Imports above.

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageSync />
      <BrowserRouter>
        <RouteAnalytics />
        {/* Phase 24.4a — global toast dispatcher mount, INSIDE BrowserRouter
            (sibling to Routes) so future toast actions can use routing
            hooks (useNavigate, useSearchParams) without breaking. */}
        <ToastContainer />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* S13.1.C — legacy /setup permanently redirects к /onboarding.
              Bookmarks keep working, RequireAuth handles un-onboarded routing. */}
          <Route path="/setup" element={<Navigate to="/onboarding" replace />} />
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <OnboardingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/billing/success"
            element={
              <RequireAuth>
                <BillingSuccessPage />
              </RequireAuth>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <RequireAuth>
                <BillingCancelPage />
              </RequireAuth>
            }
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppShell>
                  <Dashboard />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/plan"
            element={
              <RequireAuth>
                <AppShell>
                  <PlanOverview />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/plan/week/:weekNumber"
            element={
              <RequireAuth>
                <AppShell>
                  <WeekDetail />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/workout/planned/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <WorkoutDetail plannedById />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/workout/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <WorkoutDetail />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/recipes/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <RecipeDetail />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/shopping-list/:planId"
            element={
              <RequireAuth>
                <AppShell>
                  <ShoppingList />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/chat"
            element={
              <RequireAuth>
                <AppShell>
                  <ChatList />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/chat/:threadId"
            element={
              <RequireAuth>
                <AppShell>
                  <ChatThread />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/back"
            element={
              <RequireAuth>
                <AppShell>
                  <BackCheckIn />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/history"
            element={
              <RequireAuth>
                <AppShell>
                  <History />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/stats"
            element={
              <RequireAuth>
                <AppShell>
                  <Stats />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <AppShell>
                  <Settings />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/knowledge"
            element={
              <RequireAuth>
                <AppShell>
                  <KnowledgeBase />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/reviews"
            element={
              <RequireAuth>
                <AppShell>
                  <Reviews />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/nutrition"
            element={
              <RequireAuth>
                <AppShell>
                  <Nutrition />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/body"
            element={
              <RequireAuth>
                <AppShell>
                  <BodyComposition />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/nutrition/meals/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <MealDetail />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminRoute>
                  <AppShell>
                    <AdminLayout />
                  </AppShell>
                </AdminRoute>
              </RequireAuth>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="ai-spend" element={<AdminAiSpend />} />
            <Route path="costs" element={<AdminCosts />} />
            <Route path="waitlist" element={<AdminWaitlist />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="payments" element={<AdminPayments />} />
          </Route>
          <Route
            path="/nutrition/day/:date"
            element={
              <RequireAuth>
                <AppShell>
                  <NutritionDayDetail />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/nutrition/archive"
            element={
              <RequireAuth>
                <AppShell>
                  <NutritionArchive />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/workouts"
            element={
              <RequireAuth>
                <AppShell>
                  <Workouts />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AnalyticsConsent />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
